/* ==========================================================================
 * Carpet Tile Customizer
 * --------------------------------------------------------------------------
 * Single ES module, no build step. Vanilla JS. Drives a visual designer for
 * modular 50x50cm carpet tiles inside a Shopify Online Store 2.0 section.
 *
 * Loaded by sections/carpet-customizer.liquid. See docs/README.md.
 *
 * Share URLs use a compact fixed-alphabet encoding rather than a general
 * compressor: the design space is bounded (16 tiles x 4 rotations, <=100
 * cells) so one URL-safe char per cell is already smaller than LZ output and
 * needs no dependency or build step. See docs/TINKER-NOTES.md.
 * ========================================================================== */

/* ----------------------------------------------------------------------- *
 * Constants
 * ----------------------------------------------------------------------- */

const DESIGN_ORDER = ['SD', 'ST', 'LC', 'DC'];
const COLOURWAY_ORDER = ['Spring', 'Summer', 'Autumn', 'Winter'];

/** Fixed tile order used by the share codec. Independent of merchant config
 *  so old share links keep resolving even if the config is re-ordered. */
const CANONICAL_TILE_IDS = [];
for (const d of DESIGN_ORDER) {
  for (const c of COLOURWAY_ORDER) CANONICAL_TILE_IDS.push(`${d}-${c}`);
}

/** 65 URL-safe characters — one per cell state (0 empty, 1..64 painted). */
const URL_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_$';

const TILE_RASTER = 512; // offscreen raster resolution per tile (px)
const HISTORY_LIMIT = 50;
const ZOOM_MIN = 0.5;
const ZOOM_MAX = 3;
const ZOOM_STEP = 0.25;
const SNAPSHOT_MAX = 800; // longest side of the cart snapshot (px)
const STORAGE_KEY = 'carpet-sim:last-design';
const VARIANT_CACHE_KEY = 'carpet-sim:variants';
const VARIANT_CACHE_TTL = 60 * 60 * 1000; // 1 hour

const DEFAULT_CONFIG = {
  tile_size_cm: 50,
  price_per_tile_jpy: 7700,
  currency: 'JPY',
  currency_locale: 'ja-JP',
  locale: 'en',
  grid: { default_cols: 4, default_rows: 4, max_cols: 10, max_rows: 10 },
  designs: [
    { code: 'SD', name: 'Solid', rotation_affects: false },
    { code: 'ST', name: 'Stripe', rotation_affects: true },
    { code: 'LC', name: 'Light Corner', rotation_affects: true },
    { code: 'DC', name: 'Dark Corner', rotation_affects: true },
  ],
  colourways: [
    { code: 'Spring', name: 'Spring', primary: '#273673', secondary: '#36619d' },
    { code: 'Summer', name: 'Summer', primary: '#3e553d', secondary: '#667c5c' },
    { code: 'Autumn', name: 'Autumn', primary: '#808381', secondary: '#b4bbba' },
    { code: 'Winter', name: 'Winter', primary: '#c3ccd0', secondary: '#e5e0d5' },
  ],
  tiles: [
    { id: 'SD-Spring', handle: 'sd-spring' },
    { id: 'SD-Summer', handle: 'sd-summer' },
    { id: 'SD-Autumn', handle: 'sd-autumn' },
    { id: 'SD-Winter', handle: 'sd-winter' },
    { id: 'ST-Spring', handle: 'st-spring' },
    { id: 'ST-Summer', handle: 'st-summer' },
    { id: 'ST-Autumn', handle: 'st-autumn' },
    { id: 'ST-Winter', handle: 'st-winter' },
    { id: 'LC-Spring', handle: 'lc-spring' },
    { id: 'LC-Summer', handle: 'lc-summer' },
    { id: 'LC-Autumn', handle: 'lc-autumn' },
    { id: 'LC-Winter', handle: 'lc-winter' },
    { id: 'DC-Spring', handle: 'dc-spring' },
    { id: 'DC-Summer', handle: 'dc-summer' },
    { id: 'DC-Autumn', handle: 'dc-autumn' },
    { id: 'DC-Winter', handle: 'dc-winter' },
  ],
  sample_product_handle: 'sample-pack',
  sample_max_tiles: 4,
  sample_mode: 'bundled', // 'bundled' | 'per_tile'
};

/* ----------------------------------------------------------------------- *
 * Small utilities
 * ----------------------------------------------------------------------- */

const clamp = (n, lo, hi) => Math.min(hi, Math.max(lo, n));

function debounce(fn, ms) {
  let t;
  return function debounced(...args) {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), ms);
  };
}

function randomString(len) {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let out = '';
  for (let i = 0; i < len; i++) out += chars[Math.floor(Math.random() * chars.length)];
  return out;
}

function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  }[c]));
}

function safeParse(json, fallback) {
  if (typeof json !== 'string' || json.trim() === '') return fallback;
  try {
    return JSON.parse(json);
  } catch (e) {
    return fallback;
  }
}

/** Ensure a Shopify AJAX route ends in `.js`. */
function ensureJs(url, fallback) {
  if (!url) return fallback;
  return /\.js($|\?)/.test(url) ? url : url.replace(/\/?$/, '') + '.js';
}

/* ----------------------------------------------------------------------- *
 * Share codec
 * Cell value: 0 = empty; painted = 1 + tileIndex*4 + rotation  (range 1..64)
 * ----------------------------------------------------------------------- */

function encodeDesign(cols, rows, cells) {
  let out = '1' + URL_ALPHABET[cols] + URL_ALPHABET[rows];
  for (const cell of cells) {
    let v = 0;
    if (cell && cell.tile) {
      const idx = CANONICAL_TILE_IDS.indexOf(cell.tile);
      if (idx >= 0) v = 1 + idx * 4 + (cell.rot & 3);
    }
    out += URL_ALPHABET[v];
  }
  return out;
}

function decodeDesign(code) {
  if (typeof code !== 'string' || code[0] !== '1') return null;
  const cols = URL_ALPHABET.indexOf(code[1]);
  const rows = URL_ALPHABET.indexOf(code[2]);
  if (cols < 1 || rows < 1 || cols > 64 || rows > 64) return null;
  const total = cols * rows;
  if (code.length < 3 + total) return null;
  const cells = [];
  for (let i = 0; i < total; i++) {
    const v = URL_ALPHABET.indexOf(code[3 + i]);
    if (v <= 0) {
      cells.push({ tile: null, rot: 0 });
    } else {
      const idx = Math.floor((v - 1) / 4);
      cells.push({ tile: CANONICAL_TILE_IDS[idx] || null, rot: (v - 1) % 4 });
    }
  }
  return { cols, rows, cells };
}

/* ----------------------------------------------------------------------- *
 * SVG rasterization
 * ----------------------------------------------------------------------- */

async function rasterizeSvg(url) {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`SVG ${res.status}: ${url}`);
  let svg = await res.text();
  // Inject explicit pixel dimensions so the SVG decodes reliably everywhere.
  if (!/<svg[^>]*\swidth=/.test(svg)) {
    svg = svg.replace(/<svg\b/, `<svg width="${TILE_RASTER}" height="${TILE_RASTER}"`);
  }
  const blobUrl = URL.createObjectURL(new Blob([svg], { type: 'image/svg+xml' }));
  try {
    const img = new Image();
    img.decoding = 'async';
    img.src = blobUrl;
    await img.decode();
    const canvas = document.createElement('canvas');
    canvas.width = TILE_RASTER;
    canvas.height = TILE_RASTER;
    canvas.getContext('2d').drawImage(img, 0, 0, TILE_RASTER, TILE_RASTER);
    return canvas;
  } finally {
    URL.revokeObjectURL(blobUrl);
  }
}

/* ----------------------------------------------------------------------- *
 * Shopify cart / storefront helpers
 * ----------------------------------------------------------------------- */

async function fetchProductJson(handle) {
  const res = await fetch(`/products/${encodeURIComponent(handle)}.js`, {
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) throw new Error(`product ${handle}: ${res.status}`);
  return res.json();
}

async function resolveVariants(handles) {
  try {
    const cached = JSON.parse(sessionStorage.getItem(VARIANT_CACHE_KEY) || 'null');
    if (cached && cached.map && Date.now() - cached.ts < VARIANT_CACHE_TTL) {
      if (handles.every((h) => cached.map[h])) return cached.map;
    }
  } catch (e) {
    /* ignore cache read errors */
  }
  const map = {};
  const results = await Promise.allSettled(handles.map(fetchProductJson));
  results.forEach((r, i) => {
    if (r.status === 'fulfilled' && r.value && r.value.variants && r.value.variants[0]) {
      map[handles[i]] = r.value.variants[0].id;
    } else {
      console.warn('[carpet-customizer] could not resolve variant for', handles[i]);
    }
  });
  try {
    sessionStorage.setItem(VARIANT_CACHE_KEY, JSON.stringify({ ts: Date.now(), map }));
  } catch (e) {
    /* sessionStorage may be unavailable (private mode) */
  }
  return map;
}

async function cartAdd(items, addUrl) {
  const res = await fetch(ensureJs(addUrl, '/cart/add.js'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ items }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.description || data.message || 'The cart could not be updated.');
  }
  return data;
}

async function fetchCart(cartUrl) {
  try {
    const res = await fetch(ensureJs(cartUrl, '/cart.js'), {
      headers: { Accept: 'application/json' },
    });
    return res.ok ? res.json() : null;
  } catch (e) {
    return null;
  }
}

/**
 * Refresh the theme's cart UI and open the drawer.
 * Tinker's `cart-drawer-component` listens on `document` for `cart:update`
 * (its CartAddEvent) — dispatching it refreshes the cart icon + cart items
 * and auto-opens the drawer when configured. We also call the drawer's
 * public `open()` so it opens even without the `auto-open` attribute.
 */
function openCart(cart) {
  document.dispatchEvent(
    new CustomEvent('cart:update', {
      bubbles: true,
      detail: {
        resource: cart,
        sourceId: 'carpet-customizer',
        data: { source: 'carpet-customizer', itemCount: cart && cart.item_count },
      },
    })
  );
  const drawer =
    document.querySelector('cart-drawer-component') || document.querySelector('cart-drawer');
  if (drawer && typeof drawer.open === 'function') {
    try {
      drawer.open();
      return;
    } catch (e) {
      /* fall through */
    }
  }
  if (!drawer) window.location = '/cart';
}

/* ----------------------------------------------------------------------- *
 * Inline icons
 * ----------------------------------------------------------------------- */

const ICON = {
  rotate:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12a9 9 0 1 1-3-6.7"/><path d="M21 3v5h-5"/></svg>',
  fill:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 3h18v18H3z"/><path d="M3 9h18M9 3v18"/></svg>',
  trash:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14"/></svg>',
  undo:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 14 4 9l5-5"/><path d="M4 9h11a5 5 0 0 1 0 10H9"/></svg>',
  redo:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m15 14 5-5-5-5"/><path d="M20 9H9a5 5 0 0 0 0 10h6"/></svg>',
  plus:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M12 5v14M5 12h14"/></svg>',
  minus:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M5 12h14"/></svg>',
  hand:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 11V6a2 2 0 0 0-4 0M14 10V4a2 2 0 0 0-4 0v6M10 10.5V6a2 2 0 0 0-4 0v8"/><path d="M18 8a2 2 0 0 1 4 0v6a8 8 0 0 1-8 8h-2a8 8 0 0 1-7-4l-3-5a2 2 0 0 1 3.4-2L8 16"/></svg>',
  share:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><path d="m8.6 13.5 6.8 4M15.4 6.5l-6.8 4"/></svg>',
  save:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/><path d="M17 21v-8H7v8M7 3v5h8"/></svg>',
  chevron:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m6 9 6 6 6-6"/></svg>',
};

/* ----------------------------------------------------------------------- *
 * CarpetSim — one instance per section
 * ----------------------------------------------------------------------- */

class CarpetSim {
  constructor(root) {
    this.root = root;
    this.root.dataset.carpetReady = 'true';
    this.uid = root.dataset.sectionId || randomString(6);
    this.ac = new AbortController(); // for document/window listeners

    this.config = this.readConfig();
    this.assets = safeParse(root.dataset.assets, {});
    this.cartAddUrl = root.dataset.routesCartAdd || '/cart/add.js';
    this.cartUrl = root.dataset.routesCart || '/cart';
    this.showHelpers = root.dataset.showHelpers !== 'false';
    this.labels = {
      addToCart: root.dataset.addToCartLabel || 'Add design to cart',
      samples: root.dataset.sampleButtonLabel || 'Get free samples',
    };

    this.buildTileMeta();

    this.rasters = {}; // tileId -> offscreen canvas
    this.variants = {}; // handle -> variant id
    this.variantsReady = false;
    this.sampleReady = false;

    this.tileSize = this.isMobile() ? 40 : 64;
    this.state = {
      cols: this.config.grid.default_cols,
      rows: this.config.grid.default_rows,
      cells: [],
      brush: null,
      selected: 0,
      zoom: 1,
      paletteLayout: 'colourway',
    };
    this.history = [];
    this.historyIndex = -1;

    this.pointers = new Map();
    this.stroke = null;
    this.pinch = null;
    this.panState = null;
    this.renderQueued = false;
    this.sampleSelection = new Set();
    this.modalReturnFocus = null;
    this.confirmResolver = null;

    this.saveStorageDebounced = debounce(() => this.saveStorage(), 500);
    this.announceDebounced = debounce((msg) => this.announce(msg), 350);

    this.init();
  }

  /* ---- setup ---------------------------------------------------------- */

  readConfig() {
    const parsed = safeParse(this.root.dataset.config, null);
    const cfg = parsed ? { ...DEFAULT_CONFIG, ...parsed } : { ...DEFAULT_CONFIG };
    cfg.grid = { ...DEFAULT_CONFIG.grid, ...(cfg.grid || {}) };
    if (!Array.isArray(cfg.tiles) || cfg.tiles.length === 0) cfg.tiles = DEFAULT_CONFIG.tiles;
    if (!Array.isArray(cfg.designs)) cfg.designs = DEFAULT_CONFIG.designs;
    if (!Array.isArray(cfg.colourways)) cfg.colourways = DEFAULT_CONFIG.colourways;
    return cfg;
  }

  buildTileMeta() {
    const designs = {};
    for (const d of this.config.designs) designs[d.code] = d;
    const colourways = {};
    for (const c of this.config.colourways) colourways[c.code] = c;

    this.tileMeta = {};
    this.tileById = {};
    for (const tile of this.config.tiles) {
      const [dCode, cCode] = String(tile.id).split('-');
      const design = designs[dCode] || { code: dCode, name: dCode, rotation_affects: true };
      const colour = colourways[cCode] || { code: cCode, name: cCode, primary: '#999', secondary: '#ccc' };
      this.tileById[tile.id] = tile;
      this.tileMeta[tile.id] = {
        id: tile.id,
        handle: tile.handle || tile.id.toLowerCase(),
        design,
        colour,
        rotationAffects: design.rotation_affects !== false,
        label: `${design.name} ${colour.name}`,
        svgUrl: this.assets[tile.id] || tile.svg || null,
        fallback: colour.primary,
      };
    }
  }

  isMobile() {
    return window.matchMedia('(max-width: 1023px)').matches;
  }

  money(amount) {
    try {
      return new Intl.NumberFormat(this.config.currency_locale || 'ja-JP', {
        style: 'currency',
        currency: this.config.currency || 'JPY',
        maximumFractionDigits: 0,
      }).format(amount);
    } catch (e) {
      return `¥${Math.round(amount).toLocaleString()}`;
    }
  }

  async init() {
    this.buildDom();
    this.cacheRefs();
    this.bindEvents();
    this.loadInitialState();
    this.history = [];
    this.historyIndex = -1;
    this.pushHistory();
    this.renderPalette();
    this.render();
    this.root.classList.add('carpet-sim--ready');

    await this.loadAssets();
    this.renderPalette();
    this.render();

    this.resolveVariantsInBackground();
  }

  /* ---- DOM construction ---------------------------------------------- */

  buildDom() {
    const id = this.uid;
    this.root.innerHTML = `
      <div class="carpet-sim__banner" data-banner role="status"></div>
      <div class="carpet-sim__app">

        <div class="carpet-sim__palette-region">
          <div class="carpet-sim__seg" role="group" aria-label="Palette layout">
            <button type="button" data-layout="colourway" class="is-active">By colourway</button>
            <button type="button" data-layout="design">By design</button>
          </div>
          <div class="carpet-sim__palette" data-palette role="group" aria-label="Tile palette"></div>
        </div>

        <div class="carpet-sim__tools carpet-sim__panel" role="group" aria-label="Tools">
          <button type="button" class="carpet-sim__btn" data-tool="rotate">${ICON.rotate}<span>Rotate</span></button>
          <button type="button" class="carpet-sim__btn" data-tool="fill">${ICON.fill}<span>Fill all</span></button>
          <button type="button" class="carpet-sim__btn" data-tool="clear">${ICON.trash}<span>Clear</span></button>
          <button type="button" class="carpet-sim__btn" data-tool="undo">${ICON.undo}<span>Undo</span></button>
          <button type="button" class="carpet-sim__btn" data-tool="redo">${ICON.redo}<span>Redo</span></button>
        </div>

        <div class="carpet-sim__canvas-region">
          <div class="carpet-sim__viewport" data-viewport>
            <div class="carpet-sim__stage" data-stage>
              <canvas class="carpet-sim__canvas" data-canvas></canvas>
              <div class="carpet-sim__overlay" data-overlay role="grid" aria-label="Carpet design grid"></div>
              <div class="carpet-sim__prompt" data-prompt>
                <span>Pick a tile from the palette, then click or drag here to start designing.</span>
              </div>
            </div>
          </div>
          <div class="carpet-sim__zoombar">
            <button type="button" class="carpet-sim__btn carpet-sim__btn--icon" data-zoom="out" aria-label="Zoom out">${ICON.minus}</button>
            <span class="carpet-sim__zoom-value" data-zoom-value>100%</span>
            <button type="button" class="carpet-sim__btn carpet-sim__btn--icon" data-zoom="in" aria-label="Zoom in">${ICON.plus}</button>
            <button type="button" class="carpet-sim__btn" data-zoom="fit">Fit</button>
            <button type="button" class="carpet-sim__btn carpet-sim__btn--icon" data-pan-toggle aria-label="Pan mode" aria-pressed="false">${ICON.hand}</button>
          </div>
        </div>

        <div class="carpet-sim__summary carpet-sim__panel" data-summary>
          <button type="button" class="carpet-sim__summary-toggle" data-summary-toggle aria-expanded="true">
            <span class="carpet-sim__panel-title">Summary</span>${ICON.chevron}
          </button>
          <div class="carpet-sim__summary-body" data-summary-body></div>
        </div>

        <div class="carpet-sim__cta">
          <button type="button" class="carpet-sim__btn carpet-sim__btn--primary carpet-sim__btn--block" data-add-cart>${escapeHtml(this.labels.addToCart)}</button>
          <button type="button" class="carpet-sim__btn carpet-sim__btn--block carpet-sim__btn--ghost" data-samples>${escapeHtml(this.labels.samples)}</button>
          <p class="carpet-sim__cta-note">Tax and shipping calculated at checkout.</p>
        </div>

        <div class="carpet-sim__footer">
          <div class="carpet-sim__panel">
            <p class="carpet-sim__panel-title">Grid size</p>
            <div class="carpet-sim__size-controls">
              <div class="carpet-sim__field">
                <label class="carpet-sim__field-label" for="cs-cols-${id}">Columns</label>
                <div class="carpet-sim__stepper">
                  <button type="button" data-size="cols-dec" aria-label="Fewer columns">${ICON.minus}</button>
                  <input id="cs-cols-${id}" data-size-input="cols" type="number" inputmode="numeric"
                    min="1" max="${this.config.grid.max_cols}" value="${this.state.cols}">
                  <button type="button" data-size="cols-inc" aria-label="More columns">${ICON.plus}</button>
                </div>
              </div>
              <div class="carpet-sim__field">
                <label class="carpet-sim__field-label" for="cs-rows-${id}">Rows</label>
                <div class="carpet-sim__stepper">
                  <button type="button" data-size="rows-dec" aria-label="Fewer rows">${ICON.minus}</button>
                  <input id="cs-rows-${id}" data-size-input="rows" type="number" inputmode="numeric"
                    min="1" max="${this.config.grid.max_rows}" value="${this.state.rows}">
                  <button type="button" data-size="rows-inc" aria-label="More rows">${ICON.plus}</button>
                </div>
              </div>
              <div class="carpet-sim__field">
                <span class="carpet-sim__field-label">Presets</span>
                <div class="carpet-sim__presets">
                  <button type="button" class="carpet-sim__btn" data-preset="2,3">Small rug (2&times;3)</button>
                  <button type="button" class="carpet-sim__btn" data-preset="4,4">Medium (4&times;4)</button>
                  <button type="button" class="carpet-sim__btn" data-preset="8,8">Large room (8&times;8)</button>
                </div>
              </div>
            </div>
            <p class="carpet-sim__area">Area: <strong data-area>0.0 m&sup2;</strong></p>
          </div>
          <div class="carpet-sim__panel">
            <p class="carpet-sim__panel-title">Share design</p>
            <button type="button" class="carpet-sim__btn carpet-sim__btn--block" data-share>${ICON.share}<span>Copy share link</span></button>
          </div>
          <div class="carpet-sim__panel">
            <p class="carpet-sim__panel-title">Save</p>
            <button type="button" class="carpet-sim__btn carpet-sim__btn--block" data-save>${ICON.save}<span>Save to this browser</span></button>
          </div>
        </div>

        <div class="carpet-sim__mobile-bar">
          <button type="button" class="carpet-sim__btn carpet-sim__btn--ghost" data-samples>${escapeHtml(this.labels.samples)}</button>
          <button type="button" class="carpet-sim__btn carpet-sim__btn--primary" data-add-cart>${escapeHtml(this.labels.addToCart)}</button>
        </div>

      </div>

      <div class="carpet-sim__sr" aria-live="polite" data-live></div>
      <div class="carpet-sim__toasts" data-toasts aria-live="polite"></div>

      <div class="carpet-sim__modal" data-sample-modal role="dialog" aria-modal="true"
           aria-labelledby="cs-sample-title-${id}">
        <div class="carpet-sim__modal-backdrop" data-modal-dismiss="sample"></div>
        <div class="carpet-sim__modal-card" role="document">
          <h3 class="carpet-sim__modal-title" id="cs-sample-title-${id}">Request free samples</h3>
          <p class="carpet-sim__modal-text" data-sample-intro></p>
          <div class="carpet-sim__sample-grid" data-sample-grid></div>
          <p class="carpet-sim__modal-count" data-sample-count></p>
          <div class="carpet-sim__modal-actions">
            <button type="button" class="carpet-sim__btn" data-modal-dismiss="sample">Cancel</button>
            <button type="button" class="carpet-sim__btn carpet-sim__btn--primary" data-sample-add>Add samples to cart</button>
          </div>
        </div>
      </div>

      <div class="carpet-sim__modal" data-confirm-modal role="dialog" aria-modal="true"
           aria-labelledby="cs-confirm-title-${id}">
        <div class="carpet-sim__modal-backdrop" data-confirm="no"></div>
        <div class="carpet-sim__modal-card" role="document">
          <h3 class="carpet-sim__modal-title" id="cs-confirm-title-${id}" data-confirm-title></h3>
          <p class="carpet-sim__modal-text" data-confirm-text></p>
          <div class="carpet-sim__modal-actions">
            <button type="button" class="carpet-sim__btn" data-confirm="no">Cancel</button>
            <button type="button" class="carpet-sim__btn carpet-sim__btn--primary" data-confirm="yes">Continue</button>
          </div>
        </div>
      </div>
    `;
  }

  cacheRefs() {
    const q = (sel) => this.root.querySelector(sel);
    this.el = {
      banner: q('[data-banner]'),
      palette: q('[data-palette]'),
      viewport: q('[data-viewport]'),
      stage: q('[data-stage]'),
      canvas: q('[data-canvas]'),
      overlay: q('[data-overlay]'),
      prompt: q('[data-prompt]'),
      zoomValue: q('[data-zoom-value]'),
      panToggle: q('[data-pan-toggle]'),
      summary: q('[data-summary]'),
      summaryBody: q('[data-summary-body]'),
      area: q('[data-area]'),
      live: q('[data-live]'),
      toasts: q('[data-toasts]'),
      sampleModal: q('[data-sample-modal]'),
      sampleGrid: q('[data-sample-grid]'),
      sampleCount: q('[data-sample-count]'),
      sampleIntro: q('[data-sample-intro]'),
      confirmModal: q('[data-confirm-modal]'),
      confirmTitle: q('[data-confirm-title]'),
      confirmText: q('[data-confirm-text]'),
      colsInput: q('[data-size-input="cols"]'),
      rowsInput: q('[data-size-input="rows"]'),
    };
    this.ctx = this.el.canvas.getContext('2d');
  }

  /* ---- asset loading -------------------------------------------------- */

  async loadAssets() {
    const ids = Object.keys(this.tileMeta);
    const results = await Promise.allSettled(
      ids.map((tid) => {
        const url = this.tileMeta[tid].svgUrl;
        return url ? rasterizeSvg(url) : Promise.reject(new Error('no svg url'));
      })
    );
    let failed = 0;
    results.forEach((r, i) => {
      if (r.status === 'fulfilled') this.rasters[ids[i]] = r.value;
      else failed++;
    });
    if (failed > 0) {
      console.warn(`[carpet-customizer] ${failed} tile artwork file(s) failed to load.`);
    }
  }

  async resolveVariantsInBackground() {
    const handles = Object.values(this.tileMeta).map((m) => m.handle);
    const sampleHandle = this.config.sample_product_handle;
    if (sampleHandle) handles.push(sampleHandle);
    try {
      this.variants = await resolveVariants(handles);
    } catch (e) {
      this.variants = {};
    }
    const missing = Object.values(this.tileMeta)
      .map((m) => m.handle)
      .filter((h) => !this.variants[h]);
    this.variantsReady = missing.length === 0;
    this.sampleReady = !sampleHandle || Boolean(this.variants[sampleHandle]);

    if (!this.variantsReady) {
      this.showBanner(
        `Some products could not be found in this store (${missing.length} missing). ` +
          `Add to cart is disabled until every tile handle resolves. ` +
          `Check the product handles in your store match the customizer config.`
      );
    }
    if (!this.sampleReady) {
      console.warn('[carpet-customizer] sample product handle did not resolve:', sampleHandle);
    }
    this.updateControls();
  }

  /* ---- initial state -------------------------------------------------- */

  loadInitialState() {
    let design = null;
    const hash = window.location.hash || '';
    const match = hash.match(/(?:^#|&)d=([^&]+)/);
    if (match) design = decodeDesign(decodeURIComponent(match[1]));
    if (!design) {
      const stored = safeParse(localStorage.getItem(STORAGE_KEY), null);
      if (stored && Array.isArray(stored.cells)) {
        design = stored;
        if (stored.brush && this.tileMeta[stored.brush]) this.state.brush = stored.brush;
        if (stored.paletteLayout === 'design') this.state.paletteLayout = 'design';
      }
    }
    if (design) {
      this.applyDesign(design);
    } else {
      this.state.cells = this.makeCells(this.state.cols, this.state.rows, null);
    }
    this.state.selected = clamp(this.state.selected, 0, this.state.cells.length - 1);
    if (this.state.paletteLayout === 'design') {
      this.root.querySelector('[data-layout="design"]').classList.add('is-active');
      this.root.querySelector('[data-layout="colourway"]').classList.remove('is-active');
    }
  }

  makeCells(cols, rows, source) {
    const cells = [];
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const src = source && source.cells && source.cols
          ? source.cells[r * source.cols + c]
          : null;
        cells.push(this.cleanCell(src));
      }
    }
    return cells;
  }

  cleanCell(cell) {
    if (cell && cell.tile && this.tileMeta[cell.tile]) {
      return { tile: cell.tile, rot: ((Number(cell.rot) || 0) % 4 + 4) % 4 };
    }
    return { tile: null, rot: 0 };
  }

  applyDesign(design) {
    const cols = clamp(Math.round(design.cols) || this.config.grid.default_cols, 1, this.config.grid.max_cols);
    const rows = clamp(Math.round(design.rows) || this.config.grid.default_rows, 1, this.config.grid.max_rows);
    this.state.cols = cols;
    this.state.rows = rows;
    this.state.cells = this.makeCells(cols, rows, { cols: design.cols, cells: design.cells });
  }

  /* ---- history -------------------------------------------------------- */

  snapshotState() {
    return {
      cols: this.state.cols,
      rows: this.state.rows,
      cells: this.state.cells.map((c) => ({ tile: c.tile, rot: c.rot })),
    };
  }

  pushHistory() {
    this.history = this.history.slice(0, this.historyIndex + 1);
    this.history.push(this.snapshotState());
    if (this.history.length > HISTORY_LIMIT) this.history.shift();
    this.historyIndex = this.history.length - 1;
    this.saveStorageDebounced();
  }

  undo() {
    if (this.historyIndex <= 0) return;
    this.historyIndex--;
    this.restoreHistory();
    this.toast('Undo');
  }

  redo() {
    if (this.historyIndex >= this.history.length - 1) return;
    this.historyIndex++;
    this.restoreHistory();
    this.toast('Redo');
  }

  restoreHistory() {
    const entry = this.history[this.historyIndex];
    this.state.cols = entry.cols;
    this.state.rows = entry.rows;
    this.state.cells = entry.cells.map((c) => ({ tile: c.tile, rot: c.rot }));
    this.state.selected = clamp(this.state.selected, 0, this.state.cells.length - 1);
    this.syncSizeInputs();
    this.render();
    this.saveStorageDebounced();
  }

  /* ---- rendering ------------------------------------------------------ */

  scheduleRender() {
    if (this.renderQueued) return;
    this.renderQueued = true;
    requestAnimationFrame(() => {
      this.renderQueued = false;
      this.render();
    });
  }

  render() {
    this.renderCanvas();
    this.ensureOverlay();
    this.updateOverlayState();
    this.renderSummary();
    this.updateControls();
  }

  renderCanvas() {
    const { cols, rows, cells, zoom } = this.state;
    const cssCell = this.tileSize * zoom;
    const cssW = cols * cssCell;
    const cssH = rows * cssCell;

    let scale = clamp(window.devicePixelRatio || 1, 1, 2);
    const maxDim = 4096;
    if (cssW * scale > maxDim || cssH * scale > maxDim) {
      scale = Math.max(1, Math.min(maxDim / cssW, maxDim / cssH));
    }

    const canvas = this.el.canvas;
    const cw = Math.max(1, Math.round(cssW * scale));
    const ch = Math.max(1, Math.round(cssH * scale));
    // Only resize the backing store when it actually changes — assigning
    // canvas.width every frame would reallocate it and stall drag-painting.
    if (canvas.width !== cw) canvas.width = cw;
    if (canvas.height !== ch) canvas.height = ch;
    this.el.stage.style.width = `${cssW}px`;
    this.el.stage.style.height = `${cssH}px`;

    const ctx = this.ctx;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    const edge = (i) => Math.round(i * cssCell * scale);

    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const cell = cells[r * cols + c];
        if (!cell || !cell.tile) continue;
        const x0 = edge(c);
        const y0 = edge(r);
        const w = edge(c + 1) - x0;
        const h = edge(r + 1) - y0;
        const raster = this.rasters[cell.tile];
        ctx.save();
        ctx.translate(x0 + w / 2, y0 + h / 2);
        if (cell.rot) ctx.rotate((cell.rot * Math.PI) / 2);
        if (raster) {
          ctx.drawImage(raster, -w / 2, -h / 2, w, h);
        } else {
          ctx.fillStyle = this.tileMeta[cell.tile].fallback;
          ctx.fillRect(-w / 2, -h / 2, w, h);
        }
        ctx.restore();
      }
    }
  }

  ensureOverlay() {
    const { cols, rows } = this.state;
    const dims = `${cols}x${rows}`;
    if (this._overlayDims === dims) return;
    this._overlayDims = dims;

    let html = '';
    for (let r = 0; r < rows; r++) {
      html += `<div class="carpet-sim__row" role="row" style="grid-template-columns:repeat(${cols},1fr)">`;
      for (let c = 0; c < cols; c++) {
        const idx = r * cols + c;
        html +=
          `<div class="carpet-sim__cell" role="gridcell" tabindex="-1" data-index="${idx}">` +
          `<button type="button" class="carpet-sim__rotate-handle" tabindex="-1" aria-hidden="true" ` +
          `data-rotate="${idx}">${ICON.rotate}</button></div>`;
      }
      html += '</div>';
    }
    this.el.overlay.innerHTML = html;
    this.el.overlay.setAttribute('aria-rowcount', String(rows));
    this.el.overlay.setAttribute('aria-colcount', String(cols));
    this.cells = Array.from(this.el.overlay.querySelectorAll('.carpet-sim__cell'));
    this.renderRulers();
  }

  renderRulers() {
    this.el.stage.querySelectorAll('.carpet-sim__ruler').forEach((n) => n.remove());
    this.el.stage.classList.toggle('carpet-sim__stage--ruled', this.showHelpers);
    if (!this.showHelpers) return;
    const { cols, rows } = this.state;
    const top = document.createElement('div');
    top.className = 'carpet-sim__ruler carpet-sim__ruler--top';
    top.setAttribute('aria-hidden', 'true');
    for (let c = 0; c < cols; c++) top.innerHTML += `<span>${c + 1}</span>`;
    const left = document.createElement('div');
    left.className = 'carpet-sim__ruler carpet-sim__ruler--left';
    left.setAttribute('aria-hidden', 'true');
    for (let r = 0; r < rows; r++) left.innerHTML += `<span>${r + 1}</span>`;
    this.el.stage.append(top, left);
  }

  updateOverlayState() {
    const { cols, cells, selected } = this.state;
    for (let i = 0; i < this.cells.length; i++) {
      const el = this.cells[i];
      const cell = cells[i];
      const filled = Boolean(cell && cell.tile);
      el.classList.toggle('carpet-sim__cell--empty', !filled);
      el.classList.toggle('is-selected', i === selected);
      el.tabIndex = i === selected ? 0 : -1;
      el.setAttribute('aria-label', this.cellLabel(i));
      el.setAttribute('aria-selected', i === selected ? 'true' : 'false');
      const handle = el.firstElementChild;
      handle.hidden = !filled;
    }
    this.root.classList.toggle('carpet-sim--has-tiles', cells.some((c) => c.tile));
    void cols;
  }

  cellLabel(index) {
    const { cols, cells } = this.state;
    const cell = cells[index];
    const row = Math.floor(index / cols) + 1;
    const col = (index % cols) + 1;
    let state;
    if (cell && cell.tile) {
      const meta = this.tileMeta[cell.tile];
      const rotTxt =
        cell.rot && meta.rotationAffects ? `, rotated ${cell.rot * 90} degrees` : '';
      state = `${meta.label}${rotTxt}`;
    } else {
      state = 'empty';
    }
    return `Row ${row} column ${col}, ${state}. Press Enter to paint with the selected tile, R to rotate, Delete to clear.`;
  }

  renderPalette() {
    const tiles = this.orderedTiles();
    let html = '';
    for (const meta of tiles) {
      const active = meta.id === this.state.brush;
      const bg = meta.svgUrl ? `background-image:url('${escapeHtml(meta.svgUrl)}')` : `background-color:${meta.fallback}`;
      html +=
        `<button type="button" class="carpet-sim__swatch${active ? ' is-active' : ''}" ` +
        `data-tile="${escapeHtml(meta.id)}" aria-pressed="${active}" ` +
        `title="${escapeHtml(meta.label)}">` +
        `<span class="carpet-sim__swatch-img" style="${bg}"></span>` +
        `<span class="carpet-sim__swatch-label">${escapeHtml(meta.label)}</span>` +
        `</button>`;
    }
    this.el.palette.innerHTML = html;
  }

  orderedTiles() {
    const out = [];
    const byColourway = this.state.paletteLayout === 'colourway';
    const outer = byColourway ? DESIGN_ORDER : COLOURWAY_ORDER;
    const inner = byColourway ? COLOURWAY_ORDER : DESIGN_ORDER;
    for (const o of outer) {
      for (const i of inner) {
        const id = byColourway ? `${o}-${i}` : `${i}-${o}`;
        if (this.tileMeta[id]) out.push(this.tileMeta[id]);
      }
    }
    return out;
  }

  renderSummary() {
    const counts = this.tileCounts();
    const entries = Object.entries(counts).sort((a, b) => b[1] - a[1]);
    const price = this.config.price_per_tile_jpy;
    const totalTiles = entries.reduce((s, [, q]) => s + q, 0);
    const tileM = this.config.tile_size_cm / 100;
    const area = totalTiles * tileM * tileM;

    let body = '';
    if (entries.length === 0) {
      body = `<p class="carpet-sim__summary-empty">No tiles placed yet. Pick a tile and start painting.</p>`;
    } else {
      let rows = '';
      for (const [tileId, qty] of entries) {
        const meta = this.tileMeta[tileId];
        const bg = meta.svgUrl
          ? `background-image:url('${escapeHtml(meta.svgUrl)}')`
          : `background-color:${meta.fallback}`;
        rows +=
          `<tr class="carpet-sim__summary-row"><td>` +
          `<span class="carpet-sim__summary-swatch" style="${bg}"></span>${escapeHtml(meta.label)}</td>` +
          `<td>${qty}</td><td>${escapeHtml(this.money(qty * price))}</td></tr>`;
      }
      body =
        `<table class="carpet-sim__summary-table"><thead><tr>` +
        `<th>Tile</th><th>Qty</th><th>Subtotal</th></tr></thead><tbody>${rows}</tbody></table>`;
    }
    body +=
      `<div class="carpet-sim__summary-totals">` +
      `<span>Total: ${totalTiles} ${totalTiles === 1 ? 'tile' : 'tiles'} (${area.toFixed(1)} m&sup2;)</span>` +
      `<strong>${escapeHtml(this.money(totalTiles * price))}</strong></div>` +
      `<p class="carpet-sim__summary-meta">Tax and shipping calculated at checkout.</p>`;
    this.el.summaryBody.innerHTML = body;

    this.el.area.innerHTML = `${(this.state.cols * this.state.rows * tileM * tileM).toFixed(1)} m&sup2;`;

    this.announceDebounced(
      `${totalTiles} ${totalTiles === 1 ? 'tile' : 'tiles'}, ${area.toFixed(1)} square meters, ` +
        `total ${this.money(totalTiles * price)}.`
    );
  }

  tileCounts() {
    const counts = {};
    for (const cell of this.state.cells) {
      if (cell.tile) counts[cell.tile] = (counts[cell.tile] || 0) + 1;
    }
    return counts;
  }

  updateControls() {
    const tool = (name) => this.root.querySelector(`[data-tool="${name}"]`);
    tool('undo').disabled = this.historyIndex <= 0;
    tool('redo').disabled = this.historyIndex >= this.history.length - 1;

    const hasTiles = this.state.cells.some((c) => c.tile);
    this.root.querySelectorAll('[data-add-cart]').forEach((b) => {
      b.disabled = !hasTiles || !this.variantsReady;
    });
    this.root.querySelectorAll('[data-samples]').forEach((b) => {
      b.disabled = !this.sampleReady;
    });

    this.el.zoomValue.textContent = `${Math.round(this.state.zoom * 100)}%`;
    this.el.panToggle.classList.toggle('is-active', this.panMode);
    this.el.panToggle.setAttribute('aria-pressed', String(this.panMode));
    this.syncSizeInputs();
  }

  syncSizeInputs() {
    if (this.el.colsInput && document.activeElement !== this.el.colsInput) {
      this.el.colsInput.value = String(this.state.cols);
    }
    if (this.el.rowsInput && document.activeElement !== this.el.rowsInput) {
      this.el.rowsInput.value = String(this.state.rows);
    }
  }

  /* ---- painting ------------------------------------------------------- */

  cellAtPoint(clientX, clientY) {
    const rect = this.el.stage.getBoundingClientRect();
    if (rect.width === 0) return -1;
    const cssCell = this.tileSize * this.state.zoom;
    const col = Math.floor((clientX - rect.left) / cssCell);
    const row = Math.floor((clientY - rect.top) / cssCell);
    if (col < 0 || row < 0 || col >= this.state.cols || row >= this.state.rows) return -1;
    return row * this.state.cols + col;
  }

  paintCell(index) {
    if (index < 0) return false;
    if (!this.state.brush) {
      if (!this._warnedNoBrush) {
        this.toast('Select a tile from the palette first.');
        this._warnedNoBrush = true;
      }
      return false;
    }
    const prev = this.state.cells[index];
    const rot = prev && prev.tile ? prev.rot : 0;
    if (prev && prev.tile === this.state.brush && prev.rot === rot) {
      this.state.selected = index;
      return false;
    }
    this.state.cells[index] = { tile: this.state.brush, rot };
    this.state.selected = index;
    return true;
  }

  rotateCell(index) {
    const cell = this.state.cells[index];
    if (!cell || !cell.tile) return;
    cell.rot = (cell.rot + 1) % 4;
    this.state.selected = index;
    this.pushHistory();
    this.render();
    this.focusCell(index);
    const meta = this.tileMeta[cell.tile];
    this.announce(
      meta.rotationAffects
        ? `${meta.label} rotated to ${cell.rot * 90} degrees.`
        : `${meta.label} rotation recorded.`
    );
  }

  clearCell(index) {
    const cell = this.state.cells[index];
    if (!cell || !cell.tile) return;
    this.state.cells[index] = { tile: null, rot: 0 };
    this.pushHistory();
    this.render();
    this.focusCell(index);
  }

  fillAll() {
    if (!this.state.brush) {
      this.toast('Select a tile from the palette first.');
      return;
    }
    this.state.cells = this.state.cells.map(() => ({ tile: this.state.brush, rot: 0 }));
    this.pushHistory();
    this.render();
    this.toast('Filled the whole grid.');
  }

  async clearAll() {
    if (!this.state.cells.some((c) => c.tile)) return;
    const ok = await this.confirm(
      'Clear the design?',
      'This removes every tile from the grid. This can be undone.'
    );
    if (!ok) return;
    this.state.cells = this.state.cells.map(() => ({ tile: null, rot: 0 }));
    this.pushHistory();
    this.render();
    this.toast('Design cleared.');
  }

  /* ---- grid resize ---------------------------------------------------- */

  async resizeGrid(nextCols, nextRows) {
    const cols = clamp(Math.round(nextCols) || 1, 1, this.config.grid.max_cols);
    const rows = clamp(Math.round(nextRows) || 1, 1, this.config.grid.max_rows);
    if (cols === this.state.cols && rows === this.state.rows) {
      this.syncSizeInputs();
      return;
    }
    let lost = 0;
    for (let r = 0; r < this.state.rows; r++) {
      for (let c = 0; c < this.state.cols; c++) {
        if ((c >= cols || r >= rows) && this.state.cells[r * this.state.cols + c].tile) lost++;
      }
    }
    if (lost > 0) {
      const ok = await this.confirm(
        'Shrink the grid?',
        `Shrinking will remove ${lost} painted ${lost === 1 ? 'tile' : 'tiles'}. Continue?`
      );
      if (!ok) {
        this.syncSizeInputs();
        return;
      }
    }
    this.state.cells = this.makeCells(cols, rows, {
      cols: this.state.cols,
      cells: this.state.cells,
    });
    this.state.cols = cols;
    this.state.rows = rows;
    this.state.selected = clamp(this.state.selected, 0, cols * rows - 1);
    this.pushHistory();
    this.render();
  }

  /* ---- zoom / pan ----------------------------------------------------- */

  setZoom(next, snap) {
    let z = clamp(next, ZOOM_MIN, ZOOM_MAX);
    if (snap) z = clamp(Math.round(z / ZOOM_STEP) * ZOOM_STEP, ZOOM_MIN, ZOOM_MAX);
    if (Math.abs(z - this.state.zoom) < 0.001) return;
    const vp = this.el.viewport;
    const prevCell = this.tileSize * this.state.zoom;
    const anchorX = (vp.scrollLeft + vp.clientWidth / 2) / prevCell;
    const anchorY = (vp.scrollTop + vp.clientHeight / 2) / prevCell;
    this.state.zoom = z;
    this.renderCanvas();
    const newCell = this.tileSize * z;
    vp.scrollLeft = anchorX * newCell - vp.clientWidth / 2;
    vp.scrollTop = anchorY * newCell - vp.clientHeight / 2;
    this.updateControls();
  }

  fitZoom() {
    const vp = this.el.viewport;
    const z = Math.min(
      vp.clientWidth / (this.state.cols * this.tileSize),
      vp.clientHeight / (this.state.rows * this.tileSize)
    );
    this.setZoom(clamp(z, ZOOM_MIN, ZOOM_MAX));
  }

  setPanMode(on) {
    this.panMode = on;
    this.updateControls();
  }

  /* ---- events --------------------------------------------------------- */

  bindEvents() {
    const signal = this.ac.signal;
    const on = (el, type, fn, opts) => el.addEventListener(type, fn, { ...(opts || {}), signal });

    // palette
    on(this.el.palette, 'click', (e) => {
      const btn = e.target.closest('[data-tile]');
      if (btn) this.selectBrush(btn.dataset.tile);
    });
    this.root.querySelectorAll('[data-layout]').forEach((btn) => {
      on(btn, 'click', () => this.setPaletteLayout(btn.dataset.layout));
    });

    // tools
    this.root.querySelectorAll('[data-tool]').forEach((btn) => {
      on(btn, 'click', () => this.runTool(btn.dataset.tool));
    });

    // zoom + pan
    this.root.querySelectorAll('[data-zoom]').forEach((btn) => {
      on(btn, 'click', () => {
        const k = btn.dataset.zoom;
        if (k === 'in') this.setZoom(this.state.zoom + ZOOM_STEP, true);
        else if (k === 'out') this.setZoom(this.state.zoom - ZOOM_STEP, true);
        else this.fitZoom();
      });
    });
    on(this.el.panToggle, 'click', () => this.setPanMode(!this.panMode));

    // stage pointer interaction
    on(this.el.stage, 'pointerdown', (e) => this.onPointerDown(e));
    on(window, 'pointermove', (e) => this.onPointerMove(e));
    on(window, 'pointerup', (e) => this.onPointerUp(e));
    on(window, 'pointercancel', (e) => this.onPointerUp(e));
    on(this.el.viewport, 'wheel', (e) => this.onWheel(e), { passive: false });
    on(this.el.stage, 'pointermove', (e) => this.onHover(e));
    on(this.el.stage, 'pointerleave', () => this.clearHover());

    // keyboard inside the grid
    on(this.el.overlay, 'keydown', (e) => this.onGridKey(e));

    // global shortcuts within the section
    on(this.root, 'keydown', (e) => this.onSectionKey(e));

    // summary collapse (mobile)
    const sumToggle = this.root.querySelector('[data-summary-toggle]');
    on(sumToggle, 'click', () => {
      const collapsed = this.el.summary.classList.toggle('is-collapsed');
      sumToggle.setAttribute('aria-expanded', String(!collapsed));
    });

    // grid size
    this.root.querySelectorAll('[data-size]').forEach((btn) => {
      on(btn, 'click', () => this.onSizeButton(btn.dataset.size));
    });
    [this.el.colsInput, this.el.rowsInput].forEach((input) => {
      on(input, 'change', async () => {
        await this.resizeGrid(Number(this.el.colsInput.value), Number(this.el.rowsInput.value));
        this.el.colsInput.value = String(this.state.cols);
        this.el.rowsInput.value = String(this.state.rows);
      });
    });
    this.root.querySelectorAll('[data-preset]').forEach((btn) => {
      on(btn, 'click', () => {
        const [c, r] = btn.dataset.preset.split(',').map(Number);
        this.resizeGrid(c, r);
      });
    });

    // share / save
    on(this.root.querySelector('[data-share]'), 'click', () => this.share());
    on(this.root.querySelector('[data-save]'), 'click', () => {
      this.saveStorage();
      this.toast('Design saved to this browser.', 'success');
    });

    // CTAs
    this.root.querySelectorAll('[data-add-cart]').forEach((b) =>
      on(b, 'click', () => this.addToCart())
    );
    this.root.querySelectorAll('[data-samples]').forEach((b) =>
      on(b, 'click', () => this.openSampleModal())
    );

    // sample modal
    on(this.el.sampleModal, 'click', (e) => {
      if (e.target.closest('[data-modal-dismiss="sample"]')) this.closeSampleModal();
    });
    on(this.root.querySelector('[data-sample-add]'), 'click', () => this.addSamples());
    on(this.el.sampleGrid, 'click', (e) => {
      const btn = e.target.closest('[data-sample-tile]');
      if (btn) this.toggleSample(btn.dataset.sampleTile);
    });

    // confirm modal
    this.el.confirmModal.querySelectorAll('[data-confirm]').forEach((btn) => {
      on(btn, 'click', () => this.resolveConfirm(btn.dataset.confirm === 'yes'));
    });

    // modal keyboard (focus trap + escape)
    [this.el.sampleModal, this.el.confirmModal].forEach((modal) => {
      on(modal, 'keydown', (e) => this.onModalKey(e, modal));
    });

    // re-flow on breakpoint change
    on(window, 'resize', debounce(() => this.onResize(), 200));
    on(window, 'hashchange', () => this.onHashChange());
  }

  onResize() {
    const mobile = this.isMobile();
    const nextSize = mobile ? 40 : 64;
    if (nextSize !== this.tileSize) {
      this.tileSize = nextSize;
      this.renderCanvas();
    }
  }

  onHashChange() {
    const match = (window.location.hash || '').match(/(?:^#|&)d=([^&]+)/);
    if (!match) return;
    const design = decodeDesign(decodeURIComponent(match[1]));
    if (!design) return;
    this.applyDesign(design);
    this._overlayDims = null;
    this.pushHistory();
    this.render();
    this.toast('Loaded shared design.');
  }

  /* ---- pointer handlers ---------------------------------------------- */

  onPointerDown(e) {
    if (e.button != null && e.button !== 0) return;
    const handle = e.target.closest('[data-rotate]');
    if (handle) {
      e.preventDefault();
      this.rotateCell(Number(handle.dataset.rotate));
      return;
    }
    this.el.stage.setPointerCapture?.(e.pointerId);
    this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (this.pointers.size === 2) {
      this.beginPinch();
      this.stroke = null;
      return;
    }

    if (this.panMode) {
      this.panState = {
        x: e.clientX,
        y: e.clientY,
        sl: this.el.viewport.scrollLeft,
        st: this.el.viewport.scrollTop,
      };
      return;
    }

    const index = this.cellAtPoint(e.clientX, e.clientY);
    if (index < 0) return;
    if (e.shiftKey) {
      this.selectCell(index);
      return;
    }
    this._warnedNoBrush = false;
    this.stroke = { painted: false, last: -1 };
    this.applyStroke(index);
  }

  onPointerMove(e) {
    if (!this.pointers.has(e.pointerId)) return;
    this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (this.pinch && this.pointers.size >= 2) {
      this.updatePinch();
      return;
    }
    if (this.panState) {
      this.el.viewport.scrollLeft = this.panState.sl - (e.clientX - this.panState.x);
      this.el.viewport.scrollTop = this.panState.st - (e.clientY - this.panState.y);
      return;
    }
    if (this.stroke) {
      const index = this.cellAtPoint(e.clientX, e.clientY);
      if (index >= 0 && index !== this.stroke.last) this.applyStroke(index);
    }
  }

  onPointerUp(e) {
    this.pointers.delete(e.pointerId);
    if (this.pointers.size < 2) this.pinch = null;
    if (this.pointers.size === 0) {
      this.panState = null;
      if (this.stroke && this.stroke.painted) this.pushHistory();
      if (this.stroke) {
        this.render();
        this.focusCell(this.state.selected);
      }
      this.stroke = null;
    }
  }

  applyStroke(index) {
    const changed = this.paintCell(index);
    if (changed) this.stroke.painted = true;
    this.stroke.last = index;
    this.scheduleRender();
  }

  beginPinch() {
    const pts = [...this.pointers.values()];
    this.pinch = {
      dist: Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y) || 1,
      zoom: this.state.zoom,
    };
  }

  updatePinch() {
    const pts = [...this.pointers.values()];
    const dist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y) || 1;
    this.setZoom(this.pinch.zoom * (dist / this.pinch.dist));
  }

  onWheel(e) {
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      this.setZoom(this.state.zoom - Math.sign(e.deltaY) * ZOOM_STEP, true);
    }
  }

  onHover(e) {
    if (this.panMode) return;
    const index = this.cellAtPoint(e.clientX, e.clientY);
    if (index === this._hoverIndex) return;
    this.clearHover();
    if (index >= 0 && this.cells[index]) {
      this.cells[index].classList.add('is-hover');
      this._hoverIndex = index;
    }
  }

  clearHover() {
    if (this._hoverIndex != null && this.cells[this._hoverIndex]) {
      this.cells[this._hoverIndex].classList.remove('is-hover');
    }
    this._hoverIndex = null;
  }

  /* ---- keyboard ------------------------------------------------------- */

  onGridKey(e) {
    const { cols, rows } = this.state;
    let idx = this.state.selected;
    const col = idx % cols;
    const row = Math.floor(idx / cols);
    let handled = true;
    switch (e.key) {
      case 'ArrowRight':
        if (col < cols - 1) idx++;
        break;
      case 'ArrowLeft':
        if (col > 0) idx--;
        break;
      case 'ArrowDown':
        if (row < rows - 1) idx += cols;
        break;
      case 'ArrowUp':
        if (row > 0) idx -= cols;
        break;
      case 'Home':
        idx = row * cols;
        break;
      case 'End':
        idx = row * cols + (cols - 1);
        break;
      case 'Enter':
      case ' ':
        if (this.paintCell(this.state.selected)) {
          this.pushHistory();
          this.render();
          this.focusCell(this.state.selected);
        }
        break;
      case 'r':
      case 'R':
        this.rotateCell(this.state.selected);
        break;
      case 'Delete':
      case 'Backspace':
        this.clearCell(this.state.selected);
        break;
      default:
        handled = false;
    }
    if (handled) {
      e.preventDefault();
      if (idx !== this.state.selected) this.selectCell(idx);
    }
  }

  onSectionKey(e) {
    if (e.defaultPrevented) return;
    const meta = e.metaKey || e.ctrlKey;
    if (meta && e.key.toLowerCase() === 'z') {
      e.preventDefault();
      if (e.shiftKey) this.redo();
      else this.undo();
    }
  }

  /* ---- selection ------------------------------------------------------ */

  selectBrush(tileId) {
    if (!this.tileMeta[tileId]) return;
    this.state.brush = tileId;
    this._warnedNoBrush = false;
    this.renderPalette();
    this.saveStorageDebounced();
    this.announce(`${this.tileMeta[tileId].label} selected for painting.`);
  }

  setPaletteLayout(layout) {
    if (layout !== 'design' && layout !== 'colourway') return;
    this.state.paletteLayout = layout;
    this.root.querySelectorAll('[data-layout]').forEach((btn) => {
      btn.classList.toggle('is-active', btn.dataset.layout === layout);
    });
    this.renderPalette();
    this.saveStorageDebounced();
  }

  selectCell(index) {
    this.state.selected = clamp(index, 0, this.state.cells.length - 1);
    this.updateOverlayState();
    this.focusCell(this.state.selected);
  }

  focusCell(index) {
    const el = this.cells[index];
    if (el) el.focus({ preventScroll: false });
  }

  runTool(tool) {
    switch (tool) {
      case 'rotate':
        this.rotateCell(this.state.selected);
        break;
      case 'fill':
        this.fillAll();
        break;
      case 'clear':
        this.clearAll();
        break;
      case 'undo':
        this.undo();
        break;
      case 'redo':
        this.redo();
        break;
    }
  }

  onSizeButton(action) {
    const [dim, dir] = action.split('-');
    const delta = dir === 'inc' ? 1 : -1;
    const cols = this.state.cols + (dim === 'cols' ? delta : 0);
    const rows = this.state.rows + (dim === 'rows' ? delta : 0);
    this.resizeGrid(cols, rows);
  }

  /* ---- persistence / share ------------------------------------------- */

  saveStorage() {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          cols: this.state.cols,
          rows: this.state.rows,
          cells: this.state.cells,
          brush: this.state.brush,
          paletteLayout: this.state.paletteLayout,
        })
      );
    } catch (e) {
      /* storage may be full or disabled */
    }
  }

  share() {
    const code = encodeDesign(this.state.cols, this.state.rows, this.state.cells);
    const url = new URL(window.location.href);
    url.hash = `d=${code}`;
    const href = url.toString();
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(href).then(
        () => this.toast('Share link copied to clipboard.', 'success'),
        () => this.promptCopy(href)
      );
    } else {
      this.promptCopy(href);
    }
    history.replaceState(null, '', href);
  }

  promptCopy(href) {
    window.prompt('Copy this link to share your design:', href);
  }

  /* ---- snapshot ------------------------------------------------------- */

  snapshot() {
    const { cols, rows, cells } = this.state;
    const tilePx = clamp(Math.floor(SNAPSHOT_MAX / Math.max(cols, rows)), 24, 200);
    const w = cols * tilePx;
    const h = rows * tilePx;
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, w, h);
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const cell = cells[r * cols + c];
        if (!cell || !cell.tile) continue;
        const raster = this.rasters[cell.tile];
        ctx.save();
        ctx.translate(c * tilePx + tilePx / 2, r * tilePx + tilePx / 2);
        if (cell.rot) ctx.rotate((cell.rot * Math.PI) / 2);
        if (raster) {
          ctx.drawImage(raster, -tilePx / 2, -tilePx / 2, tilePx, tilePx);
        } else {
          ctx.fillStyle = this.tileMeta[cell.tile].fallback;
          ctx.fillRect(-tilePx / 2, -tilePx / 2, tilePx, tilePx);
        }
        ctx.restore();
      }
    }
    return canvas.toDataURL('image/jpeg', 0.85);
  }

  /* ---- add to cart ---------------------------------------------------- */

  async addToCart() {
    if (!this.variantsReady) {
      this.toast('Products are still loading. Please try again in a moment.', 'error');
      return;
    }
    const counts = this.tileCounts();
    const tileIds = Object.keys(counts);
    if (tileIds.length === 0) {
      this.toast('Paint some tiles before adding to cart.', 'error');
      return;
    }

    const totalTiles = tileIds.reduce((s, id) => s + counts[id], 0);
    const tileM = this.config.tile_size_cm / 100;
    const area = (totalTiles * tileM * tileM).toFixed(1);
    const designId = `design-${Date.now()}-${randomString(6)}`;
    const layout = this.state.cells
      .map((c) => (c.tile ? `${c.tile}:${c.rot}` : 'empty:0'))
      .join(',');

    let snap = '';
    try {
      snap = this.snapshot();
    } catch (e) {
      console.warn('[carpet-customizer] snapshot failed', e);
    }

    const items = [];
    const missing = [];
    for (const tileId of tileIds) {
      const meta = this.tileMeta[tileId];
      const variantId = this.variants[meta.handle];
      if (!variantId) {
        missing.push(tileId);
        continue;
      }
      items.push({
        id: Number(variantId),
        quantity: counts[tileId],
        properties: {
          _design_id: designId,
          _design_image: snap,
          _design_cols: String(this.state.cols),
          _design_rows: String(this.state.rows),
          _design_layout: layout,
          'Custom design': `${totalTiles} tiles, ${area} m²`,
        },
      });
    }
    if (missing.length || items.length === 0) {
      this.toast('Some tiles could not be matched to products. Add to cart cancelled.', 'error');
      return;
    }

    this.setBusy('[data-add-cart]', true, 'Adding…');
    try {
      await cartAdd(items, this.cartAddUrl);
      const cart = await fetchCart(this.cartUrl);
      openCart(cart);
      this.toast('Design added to cart.', 'success');
    } catch (e) {
      this.toast(e.message || 'Could not add to cart.', 'error');
    } finally {
      this.setBusy('[data-add-cart]', false, this.labels.addToCart);
    }
  }

  setBusy(selector, busy, label) {
    this.root.querySelectorAll(selector).forEach((btn) => {
      btn.disabled = busy;
      if (label != null) btn.textContent = label;
    });
    if (!busy) this.updateControls();
  }

  /* ---- sample modal --------------------------------------------------- */

  openSampleModal() {
    if (!this.sampleReady) {
      this.toast('The sample product is not available in this store.', 'error');
      return;
    }
    this.sampleSelection.clear();
    const max = this.config.sample_max_tiles || 4;
    this.el.sampleIntro.textContent = `Pick up to ${max} tiles to receive as free 10 cm × 10 cm samples.`;
    let html = '';
    for (const meta of this.orderedTiles()) {
      const bg = meta.svgUrl
        ? `background-image:url('${escapeHtml(meta.svgUrl)}')`
        : `background-color:${meta.fallback}`;
      html +=
        `<button type="button" class="carpet-sim__swatch" data-sample-tile="${escapeHtml(meta.id)}" ` +
        `aria-pressed="false" title="${escapeHtml(meta.label)}">` +
        `<span class="carpet-sim__swatch-img" style="${bg}"></span>` +
        `<span class="carpet-sim__swatch-label">${escapeHtml(meta.label)}</span></button>`;
    }
    this.el.sampleGrid.innerHTML = html;
    this.updateSampleCount();
    this.openModal(this.el.sampleModal);
  }

  toggleSample(tileId) {
    const max = this.config.sample_max_tiles || 4;
    if (this.sampleSelection.has(tileId)) {
      this.sampleSelection.delete(tileId);
    } else {
      if (this.sampleSelection.size >= max) {
        this.toast(`You can pick up to ${max} samples.`);
        return;
      }
      this.sampleSelection.add(tileId);
    }
    this.updateSampleCount();
  }

  updateSampleCount() {
    const max = this.config.sample_max_tiles || 4;
    this.el.sampleCount.textContent = `Selected: ${this.sampleSelection.size} / ${max}`;
    this.el.sampleGrid.querySelectorAll('[data-sample-tile]').forEach((btn) => {
      const picked = this.sampleSelection.has(btn.dataset.sampleTile);
      btn.classList.toggle('is-active', picked);
      btn.setAttribute('aria-pressed', String(picked));
      const full = this.sampleSelection.size >= max && !picked;
      btn.setAttribute('aria-disabled', String(full));
    });
    this.root.querySelector('[data-sample-add]').disabled = this.sampleSelection.size === 0;
  }

  async addSamples() {
    const ids = [...this.sampleSelection];
    if (ids.length === 0) return;
    const handle = this.config.sample_product_handle;
    const variantId = this.variants[handle];
    if (!variantId) {
      this.toast('The sample product is not available.', 'error');
      return;
    }
    const labels = ids.map((id) => this.tileMeta[id].label);
    let items;
    if (this.config.sample_mode === 'per_tile') {
      items = ids.map((id) => ({
        id: Number(variantId),
        quantity: 1,
        properties: { 'Sample tile': this.tileMeta[id].label, _sample_tile_ids: id },
      }));
    } else {
      items = [
        {
          id: Number(variantId),
          quantity: 1,
          properties: {
            'Samples requested': labels.join(', '),
            _sample_tile_ids: ids.join(','),
          },
        },
      ];
    }

    this.setBusy('[data-sample-add]', true, 'Adding…');
    try {
      await cartAdd(items, this.cartAddUrl);
      const cart = await fetchCart(this.cartUrl);
      this.closeSampleModal();
      openCart(cart);
      this.toast('Samples added to cart.', 'success');
    } catch (e) {
      this.toast(e.message || 'Could not add samples.', 'error');
    } finally {
      this.setBusy('[data-sample-add]', false, 'Add samples to cart');
    }
  }

  closeSampleModal() {
    this.closeModal(this.el.sampleModal);
  }

  /* ---- confirm dialog ------------------------------------------------- */

  confirm(title, text) {
    this.el.confirmTitle.textContent = title;
    this.el.confirmText.textContent = text;
    this.openModal(this.el.confirmModal);
    return new Promise((resolve) => {
      this.confirmResolver = resolve;
    });
  }

  resolveConfirm(value) {
    const resolver = this.confirmResolver;
    this.confirmResolver = null;
    this.closeModal(this.el.confirmModal);
    if (resolver) resolver(value);
  }

  /* ---- modal plumbing ------------------------------------------------- */

  openModal(modal) {
    this.modalReturnFocus = document.activeElement;
    modal.classList.add('is-open');
    const focusable = this.focusableIn(modal);
    if (focusable[0]) focusable[0].focus();
  }

  closeModal(modal) {
    modal.classList.remove('is-open');
    if (modal === this.el.confirmModal && this.confirmResolver) {
      this.confirmResolver(false);
      this.confirmResolver = null;
    }
    if (this.modalReturnFocus && this.modalReturnFocus.focus) {
      this.modalReturnFocus.focus();
    }
    this.modalReturnFocus = null;
  }

  focusableIn(modal) {
    return [...modal.querySelectorAll('button, [href], input, [tabindex]:not([tabindex="-1"])')].filter(
      (el) => !el.disabled && el.offsetParent !== null
    );
  }

  onModalKey(e, modal) {
    if (!modal.classList.contains('is-open')) return;
    if (e.key === 'Escape') {
      e.preventDefault();
      if (modal === this.el.confirmModal) this.resolveConfirm(false);
      else this.closeModal(modal);
      return;
    }
    if (e.key !== 'Tab') return;
    const focusable = this.focusableIn(modal);
    if (focusable.length === 0) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  /* ---- feedback ------------------------------------------------------- */

  toast(message, type) {
    const el = document.createElement('div');
    el.className = 'carpet-sim__toast' + (type ? ` carpet-sim__toast--${type}` : '');
    el.textContent = message;
    this.el.toasts.appendChild(el);
    setTimeout(() => {
      el.style.opacity = '0';
      setTimeout(() => el.remove(), 250);
    }, 2600);
  }

  announce(message) {
    this.el.live.textContent = '';
    // Force the live region to re-announce identical text.
    requestAnimationFrame(() => {
      this.el.live.textContent = message;
    });
  }

  showBanner(message) {
    this.el.banner.textContent = message;
    this.el.banner.classList.add('is-visible');
  }

  destroy() {
    this.ac.abort();
    this.root.removeAttribute('data-carpet-ready');
  }
}

/* ----------------------------------------------------------------------- *
 * Bootstrap — one CarpetSim per section, theme-editor aware
 * ----------------------------------------------------------------------- */

const instances = new WeakMap();

function initAll(scope) {
  const roots = (scope || document).querySelectorAll('.carpet-sim:not([data-carpet-ready])');
  roots.forEach((root) => {
    try {
      instances.set(root, new CarpetSim(root));
    } catch (e) {
      console.error('[carpet-customizer] failed to initialise', e);
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initAll());
} else {
  initAll();
}

// Shopify theme editor: sections are re-rendered on edit.
document.addEventListener('shopify:section:load', (e) => {
  initAll(e.target);
});
document.addEventListener('shopify:section:unload', (e) => {
  const root = e.target.querySelector('.carpet-sim');
  const inst = root && instances.get(root);
  if (inst) {
    inst.destroy();
    instances.delete(root);
  }
});